import React, { useState, useMemo, useEffect, useContext } from "react";
import { Plus, Search, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionsContext";
import { useCompanySettings } from "../context/CompanySettingsContext";
import { NotificationContext } from "../context/NotificationContext";
import { useTaxConfiguration } from "../hooks/useTaxConfiguration";
import { useExportSuccess } from "../context/ExportSuccessContext";
import { productAPI, customerAPI, salesAPI, categoryAPI } from "../api/apiService";
import authAxios from "../api/authAxios";
import { numberToWords } from "../utils/numberToWords";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// New Modular Components
import POSLayout from "../components/POS/POSLayout";
import POSHeader from "../components/POS/POSHeader";
import ProductCard from "../components/POS/ProductCard";
import CartSidebar from "../components/POS/CartSidebar";
import ProductDetailModal from "../components/POS/ProductDetailModal";
import CustomerSelectionModal from "../components/POS/CustomerSelectionModal";
import BarcodeScannerManager from "../components/POS/BarcodeScannerManager";
import POSCatalogueModal from "../components/POS/POSCatalogueModal";
import POSNavigationDrawer from "../components/POS/POSNavigationDrawer";
import EmptyState from "../components/EmptyState";

export default function POSBillingPage() {
  const navigate = useNavigate();
  // ... existing hooks ...
  const { user, userRole, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const { companySettings } = useCompanySettings();
  const { taxSettings } = useTaxConfiguration();
  const { addNotification } = useContext(NotificationContext);
  const exportSuccess = useExportSuccess();

  // State
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]); // Raw categories from API
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Enhanced Category State (Multi-Select)
  const [selectedFilters, setSelectedFilters] = useState(() => {
    try {
      const persisted = localStorage.getItem("pos_selected_filters");
      return persisted ? JSON.parse(persisted) : ["all", "cat_all"];
    } catch (e) {
      return ["all", "cat_all"];
    }
  });
  const [activeSubCategory, setActiveSubCategory] = useState(null); // Selected sub-category ID

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastScannedBarcode, setLastScannedBarcode] = useState(null);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [showNavigationDrawer, setShowNavigationDrawer] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // -- Fix Session Persistence --
  const [salesmanId, setSalesmanId] = useState(user?.salesman_id || "");
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    if (user?.salesman_id) {
      setSalesmanId(user.salesman_id);
    }
  }, [user]);

  // Reset pagination when category selection or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilters, activeSubCategory, searchQuery]);

  // Persist selected filters to localStorage
  useEffect(() => {
    localStorage.setItem("pos_selected_filters", JSON.stringify(selectedFilters));
  }, [selectedFilters]);

  // Fetch Data
  useEffect(() => {
    fetchPOSData();
    fetchNextInvoiceNumber();
  }, []);

  async function fetchPOSData() {
    setLoading(true);
    try {
      const [productsRes, customersRes, categoriesRes] = await Promise.all([
        productAPI.getAllProducts({ is_active: true }),
        customerAPI.getAllCustomers(),
        categoryAPI.getAllCategories()
      ]);

      // Normalize Categories
      const cats = categoriesRes.data || [];
      setCategories(cats);

      // Normalize Products
      let productsData = productsRes.data?.data?.products || productsRes.data?.results || productsRes.data || [];
      const normalizedProducts = productsData.map(p => ({
        id: p.id,
        name: p.name,
        price: parseFloat(p.unit_price || p.price || 0),
        image: p.image || null,
        category: p.category?.parent_name 
                  ? `${p.category.parent_name} > ${p.category.name}` 
                  : (p.category?.name || p.category || "Uncategorized"),
        categoryId: p.category?.id || null,
        sku: p.product_code || p.sku,
        product_type: p.product_type || "fixed",
        barcode: p.barcode || "",
        hsn: p.hsn_code || "",
        description: p.description,
        stock: p.stock || 0,
        unit: p.unit || "Piece",
        tax_rate: parseFloat(p.tax_rate) || 0,
        variants: p.variants || [],
        asn_code: p.asn_code || "",
        is_popular: p.is_popular || false,
        is_recommended: p.is_recommended || false,
        is_special: p.is_special || false
      }));
      setProducts(normalizedProducts);

      // Normalize Customers
      let customersData = customersRes.data?.data?.customers || customersRes.data?.results || customersRes.data || [];
      const normalizedCustomers = customersData.map(c => ({
        id: c.id,
        name: c.name,
        phone: c.phone,
        email: c.email
      }));
      setCustomers(normalizedCustomers);

      if (normalizedCustomers.length > 0) setSelectedCustomer(normalizedCustomers[0]);

    } catch (error) {
      console.error("Failed to load POS data", error);
      addNotification("error", "Error", "Failed to load products/customers");
    } finally {
      setLoading(false);
    }
  }

  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await authAxios.get('/api/billing/invoices/next-number/');
      if (response.data?.next_invoice_number) {
        setInvoiceNo(response.data.next_invoice_number);
      }
    } catch (e) { console.warn("Using fallback invoice number"); }
  };

  // Filtering (Hierarchical Multi-Select OR logic)
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Apply searchQuery first (AND logic)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q) ||
        p.barcode?.toLowerCase().includes(q) ||
        p.hsn?.toLowerCase().includes(q)
      );
    }

    // Now filter by selected categories/special filters (OR logic)
    filtered = filtered.filter(p => {
      return selectedFilters.some(filterKey => {
        if (filterKey === "all") {
          return true; // "All" matches everything
        }
        if (filterKey === "cat_all") {
          return true; // "All Categories" matches everything
        }
        if (filterKey === "popular") {
          return p.is_popular;
        }
        if (filterKey === "recommended") {
          return p.is_recommended;
        }
        if (filterKey === "special") {
          return p.is_special;
        }

        // Custom Category Key
        const catId = parseInt(filterKey);
        
        // If a specific subcategory is active, we check if the product matches it
        if (activeSubCategory) {
          const subId = parseInt(activeSubCategory);
          // Check if this subcategory belongs to one of the selected categories
          const parentCat = categories.find(c => c.id === catId);
          const isOurSub = parentCat?.subcategories?.some(sc => sc.id === subId) || catId === subId;
          if (isOurSub) {
            return p.categoryId === subId;
          }
        }

        // Match main category and its subcategories
        const mainCat = categories.find(c => c.id === catId);
        const subCatIds = mainCat?.subcategories?.map(sc => sc.id) || [];
        const allowedIds = [catId, ...subCatIds];
        return allowedIds.includes(p.categoryId);
      });
    });

    return filtered;
  }, [products, categories, selectedFilters, activeSubCategory, searchQuery]);

  // Derive display categories
  const displayCategories = useMemo(() => categories.filter(c => !c.parent), [categories]);
  
  // Find current sub-categories to display as visual cards
  const visibleSubCategories = useMemo(() => {
    const subCats = [];
    const selectedCatIds = categories
      .filter(c => selectedFilters.includes(String(c.id)))
      .map(c => c.id);

    selectedCatIds.forEach(id => {
      const cat = categories.find(c => c.id === id);
      if (cat?.subcategories) {
        subCats.push(...cat.subcategories);
      }
    });
    return subCats;
  }, [categories, selectedFilters]);

  // Reset activeSubCategory if it's no longer visible
  useEffect(() => {
    if (activeSubCategory) {
      const subId = parseInt(activeSubCategory);
      const allVisibleSubIds = visibleSubCategories.map(sc => sc.id);
      if (!allVisibleSubIds.includes(subId)) {
        setActiveSubCategory(null);
      }
    }
  }, [visibleSubCategories, activeSubCategory]);

  const handleToggleFilter = (filterKey) => {
    const isSelected = selectedFilters.includes(filterKey);
    if (isSelected) {
      if (selectedFilters.length <= 1) {
        addNotification("warning", "Selection Blocked", "Please select at least one category.");
        return;
      }
      setSelectedFilters(selectedFilters.filter(k => k !== filterKey));
    } else {
      setSelectedFilters([...selectedFilters, filterKey]);
    }
  };

  // Derived Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Cart Logic
  const addToCart = (productWithQty) => {
    // Determine unique id
    const cartItemId = productWithQty.cartItemId || productWithQty.id;

    // Retrieve current values outside state setter to avoid side effects in updater callback
    const existing = cart.find(item => item.cartItemId === cartItemId || (!item.cartItemId && item.id === productWithQty.id));
    const currentQty = existing ? parseFloat(existing.qty || 0) : 0;
    const addedQty = parseFloat(productWithQty.qty || 0);
    const requestedQty = currentQty + addedQty;

    // Stock Check
    if (requestedQty > parseFloat(productWithQty.stock)) {
      addNotification("error", "Stock Limit", `Only ${productWithQty.stock} ${productWithQty.unit || 'units'} available!`);
      return;
    }

    setCart(prev => {
      const existingInPrev = prev.find(item => item.cartItemId === cartItemId || (!item.cartItemId && item.id === productWithQty.id));
      if (existingInPrev) {
        return prev.map(item =>
          (item.cartItemId === cartItemId || (!item.cartItemId && item.id === productWithQty.id))
            ? { ...item, qty: requestedQty, notes: productWithQty.notes }
            : item
        );
      }
      return [...prev, { ...productWithQty, qty: addedQty, cartItemId, discount: 0 }];
    });
  };

  // Refined addToCartwrapper due to closure scope of cart? No, setCart `prev` is safe.
  // But to trigger notification only on success is tricky.
  // It's better to check against `cart` state if we depend on it, but `cart` might be stale in closure?
  // `addToCart` is recreated on render so `cart` is fresh? No, `fetched` logic doesn't depend on cart.
  // But `addToCart` is NOT wrapped in useCallback, so it captures current `cart`.

  // const existing = cart.find(item => item.id === productWithQty.id);
  // const currentQty = existing ? existing.qty : 0;

  // if (currentQty + productWithQty.qty > productWithQty.stock) {
  //     addNotification("error", "Stock Limit", `Only ${productWithQty.stock} available`);
  //     return;
  // }

  // setCart(prev => {
  //     // ... update logic
  // });
  // addNotification("success", "Added", ...);


  // Re-implementing addToCart below in ReplaceContent correctly


  const updateQty = (cartItemId, newQty) => {
    // If input is empty string, keep the item but set qty to empty (handles typing)
    if (newQty === "") {
      setCart(prev => prev.map(item => (item.cartItemId || item.id) === cartItemId ? { ...item, qty: "" } : item));
      return;
    }

    const qty = parseFloat(newQty);
    if (isNaN(qty)) return; // Ignore invalid non-numeric typing

    if (qty <= 0) {
      setCart(prev => prev.filter(item => (item.cartItemId || item.id) !== cartItemId));
    } else {
      setCart(prev => prev.map(item => (item.cartItemId || item.id) === cartItemId ? { ...item, qty: qty } : item));
    }
  };

  const updateItemDiscount = (cartItemId, discountAmount) => {
    setCart(prev => prev.map(item => (item.cartItemId || item.id) === cartItemId ? { ...item, discount: discountAmount } : item));
  };

  // New State for Enhanced UI
  const [billingType, setBillingType] = useState("Walk-in Bill");
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [gstEnabled, setGstEnabled] = useState(true); // Default to true for most shops
  const [taxMode, setTaxMode] = useState("exclusive"); // Default to exclusive to add GST on top of total

  // Sync Salesman ID when user user updates (async)
  // Sync Salesman ID when user user updates (async) -> Already handled above

  // Billing Type Logic - The Core Rule
  useEffect(() => {
    switch (billingType) {
      case "Walk-in Bill":
        setPaymentMode("Cash");
        setGstEnabled(true); // Enable GST for walk-ins by default
        setSelectedCustomer(null);
        break;
      case "Customer Bill":
        // Defaults, but editable
        if (paymentMode === "Pending") setPaymentMode("Cash");
        break;
      case "Credit / Pending Bill":
        setPaymentMode("Pending");
        break;
      case "GST Bill":
        setGstEnabled(true);
        break;
      case "Non-GST Bill":
        setGstEnabled(false);
        break;
      default:
        break;
    }
  }, [billingType]);

  const handleLogout = () => {
    setConfirmAction({
      message: "Are you sure you want to logout?",
      onConfirm: () => {
        logout();
        navigate('/login');
      }
    });
  };

  const handleClearCart = () => {
    setConfirmAction({
      message: "Clear entire cart?",
      onConfirm: () => {
        setCart([]);
      }
    });
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      addNotification("error", "Empty Cart", "Please add items to cart");
      return;
    }

    // Validation Rules
    if (billingType === "Credit / Pending Bill" && !selectedCustomer) {
      addNotification("error", "Validation Error", "Customer details required for credit bill");
      setShowCustomerModal(true);
      return;
    }

    if (billingType === "Customer Bill" && !selectedCustomer) {
      addNotification("error", "Validation Error", "Select a customer for Customer Bill");
      setShowCustomerModal(true);
      return;
    }

    try {
      // Logic with item discounts
      const subtotal = cart.reduce((sum, item) => sum + ((item.price * item.qty) - (item.discount || 0)), 0);
      const effectiveTaxRate = gstEnabled ? (taxSettings?.gst_percentage || 18) : 0; // Default GST 18% if enabled but not configured

      // Note: Backend handles tax calculation if 'tax_rate' is passed. 
      // We pass tax_rate effectively.

      const invoiceData = {
        customer: selectedCustomer?.id || null, // Allow null for walk-in
        customer_id: selectedCustomer?.id || null, // Backend looking for customer_id? Serializer usually accepts 'customer' as ID or object. Let's send 'customer' as ID.

        status: "completed",
        payment_status: paymentMode === "Pending" ? "unpaid" : "paid", // Backend expects 'paid' or 'unpaid' usually, or 'pending' if mapped. let's check backend enum. Backend seems to use 'paid', 'unpaid', 'partial'. 'pending' might be 'unpaid'.
        // Backend View line 275: requested_payment_status = self.request.data.get('payment_status', 'unpaid')

        billing_mode: gstEnabled ? 'with_gst' : 'without_gst', // Backend expects 'billing_mode'
        tax_mode: taxMode,
        payment_mode: paymentMode, // Cash, Card, UPI, etc.

        // Tax Rate
        // Tax Rate (Send the calculated rate if consistent, otherwise 0 as backend will aggregate from items)
        tax_rate: typeof displayTaxRate === 'number' ? displayTaxRate : 0,

        // Map items
        items: cart.map(i => ({
          id: i.id, // Base product ID
          variant_id: i.variant_id || null,
          variant_name: i.variant_name || '',
          qty: i.qty,
          price: parseFloat(i.price).toFixed(2), // Unit Price
          name: i.name,
          sku: i.sku,
          discount: parseFloat(i.discount || 0).toFixed(2),
          tax: parseFloat(i.tax_rate || 0).toFixed(2),
          unit: i.unit || (i.product_type === 'weight_based' ? 'kg' : 'units')
        }))
      };

      if (paymentMode === "Pending") {
        invoiceData.payment_status = 'unpaid';
      }

      console.log("Sending Order to Backend:", invoiceData);

      // Actual API Call
      const response = await salesAPI.createSale(invoiceData);

      console.log("Order Success:", response.data);

      addNotification("success", "Order Placed", `Invoice ${response.data.invoice_number} generated!`);

      // Navigate to Success Page with REAL data from Backend
      navigate('/pos/invoice-success', {
        state: {
          id: response.data.id,
          invoiceNo: response.data.invoice_number,
          totalAmount: response.data.total_amount,
          subtotal: response.data.subtotal,
          taxAmount: (parseFloat(response.data.cgst_amount) || 0) + (parseFloat(response.data.sgst_amount) || 0) + (parseFloat(response.data.igst_amount) || 0),
          cgst: parseFloat(response.data.cgst_amount) || 0,
          sgst: parseFloat(response.data.sgst_amount) || 0,
          igst: parseFloat(response.data.igst_amount) || 0,
          taxRate: response.data.tax_rate,
          gst_summary: response.data.gst_summary,
          items: cart, // Keep cart items for display, but totals come from backend
          customer: selectedCustomer,
          billingType: billingType,
          billingMode: response.data.billing_mode, // 'with_gst' or 'without_gst'
          successDetails: {
            paidAmount: response.data.paid_amount,
            balanceAmount: response.data.total_amount - response.data.paid_amount,
            paymentMethod: paymentMode
          }
        }
      });

      setCart([]);
      fetchNextInvoiceNumber();

      // Reset to default for next bill (optional)
      if (!billingType.includes("Walk-in")) {
        setBillingType("Walk-in Bill");
      }

    } catch (e) {
      console.error("Order Failed:", e);
      // Extract backend error message if available
      const errorMsg = e.response?.data?.detail || e.response?.data?.message || "Failed to place order";
      addNotification("error", "Error", errorMsg);
    }
  };

  // Barcode Scanning Logic
  const handleBarcodeScan = (barcode) => {
    console.log("Barcode Scanned:", barcode);
    
    let foundProduct = products.find(p => p.barcode === barcode);
    let foundVariant = null;
    
    // If not found as main product, search in variants
    if (!foundProduct) {
        for (const p of products) {
            const variant = p.variants?.find(v => v.barcode === barcode);
            if (variant) {
                foundProduct = p;
                foundVariant = variant;
                break;
            }
        }
    }

    if (foundProduct) {
      const stock = foundVariant ? foundVariant.stock : foundProduct.stock;
      const itemName = foundVariant ? `${foundProduct.name} - ${foundVariant.name}` : foundProduct.name;
      
      if (stock <= 0) {
        addNotification("error", "Out of Stock", `${itemName} is currently out of stock.`);
        return;
      }

      // Play scanner beep sound (Professional Double Chime)
      const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/598/598-preview.mp3");
      audio.volume = 0.8;
      audio.play().catch(e => console.log("Audio play failed", e));

      // Add to cart with qty 1
      addToCart({
        ...foundProduct,
        qty: 1,
        price: foundVariant ? Number(foundVariant.unit_price) : foundProduct.price,
        variant_id: foundVariant?.id,
        variant_name: foundVariant?.name,
        cartItemId: foundVariant ? `${foundProduct.id}-${foundVariant.id}` : foundProduct.id,
        stock: stock
      });
      addNotification("success", "Item Scanned", `${itemName} added to cart`);
      setLastScannedBarcode(null); // Clear any previous error
    } else {
      console.warn("Product not found for barcode:", barcode);
      setLastScannedBarcode(barcode);
      addNotification("error", "Product Not Found", `Barcode: ${barcode}`);
    }
  };

  // ... rest of effect hooks ...

  // Calculations
  const rawSubtotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const totalItemDiscount = cart.reduce((sum, item) => sum + (parseFloat(item.discount) || 0), 0);
  const subtotal = rawSubtotal - totalItemDiscount;
  
  // Dynamic Tax Rate based on Billing Mode (GST enabled/disabled)
  const currentTaxRate = gstEnabled ? (taxSettings?.gst_percentage || 18) : 0;
  
  // Dynamic Tax Calculation based on individual item rates
  let tax = 0;
  let total = subtotal;

  if (gstEnabled) {
    tax = cart.reduce((acc, item) => {
      const itemTaxRate = parseFloat(item.tax_rate) ?? currentTaxRate;
      const itemSubtotal = (item.price * item.qty) - (item.discount || 0);
      
      if (taxMode === 'inclusive') {
        // S = B + B*(R/100) => T = S - S/(1 + R/100)
        return acc + (itemSubtotal - (itemSubtotal / (1 + itemTaxRate / 100)));
      } else {
        return acc + (itemSubtotal * (itemTaxRate / 100));
      }
    }, 0);

    if (taxMode === 'exclusive') {
      total = subtotal + tax;
    } else {
      total = subtotal; // Already inclusive
    }
  }

  // Determine display tax rate (if all items have same rate, show it, otherwise show "Mixed")
  const displayTaxRate = useMemo(() => {
    if (cart.length === 0) return 0;
    const uniqueRates = [...new Set(cart.map(item => parseFloat(item.tax_rate) || 0))];
    return uniqueRates.length === 1 ? uniqueRates[0] : "Mixed";
  }, [cart, currentTaxRate]);

  // Apply Rounding if configured
  if (companySettings?.billing_settings?.invoice_round_off) {
    total = Math.round(total);
  }

  // Render
  return (
    <>
      <POSLayout
        leftPanel={
          <div className="flex flex-col h-full">
            <POSHeader
              onOpenMenu={() => setShowNavigationDrawer(true)}
              selectedFilters={selectedFilters}
              onToggleFilter={handleToggleFilter}
              activeSubCategory={activeSubCategory}
              onSubCategoryChange={setActiveSubCategory}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              categories={displayCategories}
              fullCategories={categories}
              salesmanId={salesmanId}
              setSalesmanId={setSalesmanId}
              onLogout={handleLogout}
              userRole={userRole}
              parentPhone={user?.parent_phone || companySettings?.phone}
              isSalesman={userRole === 'SALES_EXECUTIVE' || !!user?.salesman_id}
              onOpenCatalogue={() => setShowCatalogue(true)}
              onCloseOrder={() => {
                if (userRole === 'SALES_EXECUTIVE') {
                  window.location.reload();
                } else {
                  navigate('/owner/dashboard');
                }
              }}
            />

            {/* Product Display Area */}
            <div className="flex-1 overflow-y-auto p-6 pt-6">
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 pb-20">
                {paginatedProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onClick={setSelectedProduct}
                  />
                ))}
              </div>

              {paginatedProducts.length === 0 && (
                <EmptyState
                  preset="pos"
                  title="No items found in this section"
                  message="Try a different category or search term"
                />
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 py-4 mt-auto bg-white/50 backdrop-blur-sm rounded-xl shrink-0">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 px-4 rounded-lg border border-gray-200 hover:bg-white bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-gray-600 text-sm flex items-center gap-2"
                >
                  <span>Prev</span>
                </button>
                <span className="text-sm font-bold text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 px-4 rounded-lg border border-gray-200 hover:bg-white bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-gray-600 text-sm flex items-center gap-2"
                >
                  <span>Next</span>
                </button>
              </div>
            )}
          </div>
        }
        rightPanel={
          <CartSidebar
            cart={cart}
            customer={selectedCustomer}
            onSelectCustomer={() => setShowCustomerModal(true)}
            onRemoveCustomer={() => setSelectedCustomer(null)}
            onUpdateQty={updateQty}
            onUpdateDiscount={updateItemDiscount} // Passed Here
            subtotal={rawSubtotal}
            discount={totalItemDiscount}
            tax={tax}
            total={total}
            onRemoveItem={(id) => updateQty(id, 0)}
            onPlaceOrder={handlePlaceOrder}
            // New Props
            billingType={billingType}
            setBillingType={setBillingType}
            paymentMode={paymentMode}
            setPaymentMode={setPaymentMode}
            gstEnabled={gstEnabled}
            setGstEnabled={setGstEnabled}
            taxMode={taxMode}
            setTaxMode={setTaxMode}
            taxRate={displayTaxRate}
            invoiceNo={invoiceNo}
            onClearCart={handleClearCart}
            onProceedToPayment={() => {
              navigate('/pos/payment', {
                state: {
                  total: total,
                  subtotal: subtotal,
                  tax: tax,
                  items: cart,
                  customer: selectedCustomer,
                  invoiceNo: invoiceNo,
                  gstEnabled: gstEnabled,
                  taxMode: taxMode,
                  taxRate: displayTaxRate,
                  billingType: billingType
                }
              });
            }}
          />
        }
      />

      {/* Modal */}
      <ProductDetailModal
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct}
        onAddToCart={addToCart}
      />
      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        customers={customers}
        onSelect={setSelectedCustomer}
        onAddNew={() => {
          setShowCustomerModal(false);
          navigate('/customers');
        }}
      />

      <POSCatalogueModal
        isOpen={showCatalogue}
        onClose={() => setShowCatalogue(false)}
        products={products}
        user={user}
        customers={customers}
      />


      <BarcodeScannerManager onScan={handleBarcodeScan} />

      {/* Product Not Found / Create Shortcut */}
      {lastScannedBarcode && (
        <div className="fixed bottom-24 right-8 z-[100] animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="bg-white rounded-2xl shadow-2xl border border-red-100 p-6 max-w-sm flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <Search className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h4 className="font-black text-gray-900 leading-tight">Product Not Found</h4>
                <p className="text-xs font-bold text-gray-400 mt-0.5">Barcode: {lastScannedBarcode}</p>
              </div>
              <button
                onClick={() => setLastScannedBarcode(null)}
                className="ml-auto w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => {
                setLastScannedBarcode(null);
                navigate('/inventory', { state: { initialBarcode: lastScannedBarcode, showCreate: true } });
              }}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-100"
            >
              <Plus className="w-4 h-4" />
              <span>Create Product Now</span>
            </button>
          </div>
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-dark-card rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden border border-gray-100 dark:border-dark-border"
          >
            <div className="p-6 text-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Confirm Action</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 font-medium">
                {confirmAction.message}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-dark-border dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl transition-all cursor-pointer text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmAction.onConfirm();
                    setConfirmAction(null);
                  }}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 cursor-pointer text-sm"
                >
                  OK
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <POSNavigationDrawer
        isOpen={showNavigationDrawer}
        onClose={() => setShowNavigationDrawer(false)}
      />
    </>
  );
}
